'use client'

/**
 * FuelPlan — recommended fueling sequence for the active route.
 *
 * Sits below the StateBreakdown and explains *where to actually buy* fuel
 * given the vehicle's tank capacity and starting fuel level. The blended
 * per-state cost is what you pay on average; this is what you pay if you
 * play your fueling smart vs. if you don't.
 *
 * Visual language matches StateBreakdown — same fixed-grid columns, same
 * font-mono, same gridlines. Numbered stops correspond to numbered map
 * markers in route-map.tsx (when those land in step 6).
 *
 * Hidden when:
 *   - Plan is infeasible (e.g., a single state longer than tank range)
 *   - No stops recommended AND no savings vs naive (full tank covers route)
 *
 * The "savings header" is what justifies the entire feature being on screen.
 * If we computed savings of $0, the panel collapses to a one-liner explainer
 * rather than wasting screen space.
 */

import { STATE_NAMES } from '@/lib/constants'
import type { FuelStopPlan } from '@/lib/fuel-stops'

interface FuelPlanProps {
	plan: FuelStopPlan
	tankCapacityGallons: number
}

/**
 * Same fixed grid template as StateBreakdown so the plan reads as a sibling
 * panel — gives the page a single locked column rhythm rather than two
 * different mini-grids fighting each other.
 *
 * Columns:
 *   [stop number] [state name flex] [mile] [price+/g] [gallons+gal] [cost]
 */
const ROW_GRID =
	'grid grid-cols-[1.5rem_minmax(0,1fr)_3.75rem_4.5rem_3.75rem_4rem] items-baseline gap-x-0 text-xs font-mono'

export function FuelPlan({ plan, tankCapacityGallons }: FuelPlanProps) {
	// Infeasible — surface a clear single-line explanation
	if (plan.infeasibleReason) {
		return (
			<div className="rounded-md border border-warning/30 bg-warning/5 px-3 py-2.5">
				<p className="text-xs text-text-secondary">
					<span className="font-semibold text-warning">Fuel plan unavailable:</span>{' '}
					{plan.infeasibleReason}. Try a larger tank capacity or break the route into shorter
					segments.
				</p>
			</div>
		)
	}

	// No stops needed AND no savings — the trip fits comfortably on starting fuel
	if (plan.stops.length === 0 && plan.savings === 0) {
		return (
			<div className="rounded-md border border-border/50 bg-surface/50 px-3 py-2.5">
				<p className="text-xs text-text-muted">
					<span className="font-semibold text-text-secondary">Smart Fuel Plan:</span> Your{' '}
					{tankCapacityGallons}-gallon tank covers this route without refueling. No stops needed —
					assuming you start with a full tank.
				</p>
			</div>
		)
	}

	return (
		<div>
			<div className="mb-2">
				<span className="text-xs font-medium text-text-secondary">Smart Fuel Plan</span>
				{/* One-line headline so a first-time reader knows what they're
				    looking at without scanning the table. Deliberately doesn't
				    claim a dollar amount up front — the comparison is shown in
				    the totals rows where the methodology is also visible. */}
				<p className="mt-0.5 text-[11px] text-text-muted">
					Stop in these zones to lock in cheaper fuel before more expensive stretches.
				</p>
			</div>

			<div className="space-y-0">
				{plan.stops.map((stop) => {
					// Color coding: smart top-off = success green, forced fill = warning amber.
					// Mirrors the marker colors planned for the map (step 6).
					const isSmart = stop.kind === 'smart'
					const indicatorColor = isSmart ? 'bg-success' : 'bg-warning'
					const kindLabel = isSmart ? 'smart' : 'forced'
					const kindTitle = isSmart
						? 'Smart top-off — proactive fill in a cheap zone before more expensive territory'
						: 'Forced fill — required to avoid running dry, no cheaper option in range'

					return (
						<div
							key={`${stop.index}-${stop.mileFromStart}`}
							className={`${ROW_GRID} border-b border-border/50 py-1.5 last:border-b-0 text-text-muted`}
						>
							{/* Numbered marker — matches the map pin number */}
							<span
								className="inline-flex size-4 items-center justify-center rounded-full text-[10px] font-bold text-background"
								title={kindTitle}
							>
								<span
									className={`flex size-4 items-center justify-center rounded-full ${indicatorColor}`}
								>
									{stop.index}
								</span>
							</span>
							{/* State name + smart/forced kind tag */}
							<span className="text-sm font-medium text-text-primary truncate pr-2 border-r border-border/30">
								{STATE_NAMES[stop.stateCode] ?? stop.state}
								<span className="ml-1.5 text-[9px] uppercase tracking-wider text-text-muted font-medium">
									{kindLabel}
								</span>
							</span>
							{/* Mile position */}
							<span className="text-right px-2 border-r border-border/30">
								{stop.mileFromStart.toLocaleString()}
								<span className="text-text-muted"> mi</span>
							</span>
							{/* Price */}
							<span className="text-right px-2 border-r border-border/30">
								${stop.pricePerGallon.toFixed(2)}
								<span className="text-text-muted">/g</span>
							</span>
							{/* Gallons */}
							<span className="text-right px-2 border-r border-border/30">
								{stop.gallons.toLocaleString()}
								<span className="text-text-muted"> gal</span>
							</span>
							{/* Cost */}
							<span className="text-right font-semibold text-text-primary pl-2">
								${stop.cost.toFixed(2)}
							</span>
						</div>
					)
				})}
			</div>

			{/* Stacked totals — main plan total prominent, on-the-fly comparison
			    smaller below. The whole section is the smart plan, so the main
			    "Total" doesn't need a "smart" qualifier; the secondary line
			    gives directional context without competing visually. */}
			<div className="mt-3 pt-2 border-t border-border">
				<div className="flex items-baseline justify-between gap-3">
					<span className="text-sm font-semibold text-text-primary">Total</span>
					<span className="font-mono text-base font-semibold text-text-primary">
						${plan.smartTotalCost.toFixed(2)}
					</span>
				</div>
				{plan.savings > 0 && (
					<div className="mt-0.5 flex items-baseline justify-between gap-3">
						<span className="text-[11px] text-text-muted">Refueling on the fly would cost</span>
						<span className="font-mono text-[11px] text-text-muted">
							~${plan.naiveTotalCost.toFixed(2)}
						</span>
					</div>
				)}
			</div>

			{/* Honest one-liner — comparison is directional, not a promise */}
			<p className="mt-2 text-[10px] leading-snug text-text-muted">
				Comparison is a directional estimate. Within each state, individual stations differ by
				$0.20–$0.40/g, so actual spend depends on driving style and which exits you pick.
			</p>
		</div>
	)
}
